import { Descriptions, Tag, Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import type { I18nContextValue } from "../../app/i18n/useI18n";
import { formatDateTimeNoSeconds } from "../../app/units/format";
import type { UnitsSettings } from "../../app/units/units";
import { letterProviderRouteLabel } from "./letterProviderRouteLabel";
import { CHANNEL_EMAIL_LC, CHANNEL_SMS, CHANNEL_WHATSAPP } from "../../app/constants/communicationChannel";

const SMS_CHANNEL_KEY = CHANNEL_SMS.toLowerCase();
const WHATSAPP_CHANNEL_KEY = CHANNEL_WHATSAPP.toLowerCase();

/**
 * Wie es der Sendung ergangen ist: Weg, Zeitpunkte, Freigabe, Fehler.
 *
 * Bewusst **ohne** den Nachrichtentext — der steht in der Dokumentansicht, und dort lesbar statt
 * als Markup. Und ohne Felder, die auf dem jeweiligen Kanal nichts bedeuten: Eine E-Mail hat keine
 * Telefonnummer, ein Objektschlüssel beantwortet keine Frage, die hier gestellt wird.
 */

type OutboundMessageDetailsRow = {
  channel: string;
  sourceType: string;
  status: string;
  studioName?: string | null;
  recipientName?: string | null;
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  subject?: string | null;
  documentTitle?: string | null;
  attachmentFileName?: string | null;
  providerIntegrationDisplayName?: string | null;
  providerIntegrationType?: string | null;
  externalMessageId?: string | null;
  errorMessage?: string | null;
  createdByDisplayName?: string | null;
  approvedAt?: string | null;
  queuedAt?: string | null;
  sentAt?: string | null;
  createdAt: string;
  retryCount?: number | null;
  lastRetriedAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string | null;
};

type Props = {
  row: OutboundMessageDetailsRow;
  units: UnitsSettings;
  text: I18nContextValue["text"];
  statusColor: (status: string) => string;
};

export function OutboundMessageDetails({ row, units, text, statusColor }: Props) {
  const when = (value?: string | null) => (value ? formatDateTimeNoSeconds(value, units) : text("—"));
  const channel = String(row.channel || "").toLowerCase();
  /** Auf welchem Weg der Empfänger erreicht wird — danach richtet sich, welche Angabe zählt. */
  const recipientAddress = channel === CHANNEL_EMAIL_LC
    ? row.recipientEmail
    : channel === SMS_CHANNEL_KEY || channel === WHATSAPP_CHANNEL_KEY
      ? row.recipientPhone
      : null;

  const items = [
    {
      key: "status",
      label: text("Status"),
      children: <Tag color={statusColor(row.status)}>{row.status}</Tag>,
    },
    { key: "channel", label: text("Channel"), children: row.channel },
    { key: "sourceType", label: text("Source"), children: row.sourceType },
    { key: "studio", label: text("Studio"), children: row.studioName || text("—") },
    { key: "recipientName", label: text("Recipient name"), children: row.recipientName || text("—") },
    // Der Brief trägt seine Anschrift im Dokument; eine leere Zeile „Adresse: —" sagte nichts.
    ...(recipientAddress != null
      ? [{
          key: "recipientAddress",
          label: channel === CHANNEL_EMAIL_LC ? text("Email") : text("Phone"),
          children: recipientAddress || text("—"),
        }]
      : []),
    { key: "subject", label: text("Subject"), children: row.subject || row.documentTitle || text("—") },
    ...(row.attachmentFileName
      ? [{ key: "attachment", label: text("Attachment"), children: row.attachmentFileName }]
      : []),
    { key: "createdAt", label: text("Created"), children: when(row.createdAt) },
    ...(row.createdByDisplayName
      ? [{ key: "createdBy", label: text("Created by"), children: row.createdByDisplayName }]
      : []),
    ...(row.approvedAt
      ? [{ key: "approvedAt", label: text("Approved at"), children: when(row.approvedAt) }]
      : []),
    { key: "queuedAt", label: text("Queued at"), children: when(row.queuedAt) },
    { key: "sentAt", label: text("Sent"), children: when(row.sentAt) },
    ...(row.cancelledAt
      ? [{
          key: "cancelledAt",
          label: text("Cancelled at"),
          children: row.cancelReason ? `${when(row.cancelledAt)} · ${row.cancelReason}` : when(row.cancelledAt),
        }]
      : []),
    // Ein Versand ohne Wiederholung ist der Regelfall; die Zeile erscheint erst, wenn es eine gab.
    ...((row.retryCount ?? 0) > 0
      ? [{
          key: "retries",
          label: text("Retries"),
          children: row.lastRetriedAt
            ? `${row.retryCount} · ${when(row.lastRetriedAt)}`
            : String(row.retryCount),
        }]
      : []),
    ...(row.providerIntegrationDisplayName || row.providerIntegrationType
      ? [{
          key: "provider",
          label: text("Provider"),
          children: letterProviderRouteLabel(
            row.providerIntegrationDisplayName,
            row.providerIntegrationType,
            text
          ),
        }]
      : []),
    ...(row.externalMessageId
      ? [{ key: "externalId", label: text("External message id"), children: row.externalMessageId }]
      : []),
    ...(row.errorMessage
      ? [{
          key: "error",
          label: text("Error"),
          children: <Typography.Text type="danger">{row.errorMessage}</Typography.Text>,
        }]
      : []),
  ];

  return <Descriptions column={1} size="small" bordered items={items} />;
}
